/**
 * Advanced search dialog — searches the whole CRM (customers, leads, emails,
 * notes, tickets, documents, helpful links) by keywords and quoted phrases.
 *
 * Opens via:
 *   • the magnifying-glass button in the top header
 *   • the "Advanced search" button under the sidebar Jump-to-page input
 *   • global keyboard shortcut Cmd/Ctrl + Shift + F
 *
 * Result groups are rendered with up to 5 hits each — clicking jumps straight
 * to the relevant record. Quoted phrases like "final invoice" trigger an
 * exact-substring match on the backend.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent,
} from './ui/dialog';
import { Search, X, Loader2, ArrowRight, Quote, Info, ExternalLink } from 'lucide-react';
import api from '../lib/api';

// Tiny helper — highlight every matched term in the rendered text so the eye
// can spot the hit instantly. Returns React children, not a string, to keep
// it XSS-safe.
const Highlight = ({ text, terms }) => {
  if (!text) return null;
  if (!terms?.length) return <>{text}</>;
  const safeTerms = terms.filter(Boolean).map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  if (safeTerms.length === 0) return <>{text}</>;
  const re = new RegExp(`(${safeTerms.join('|')})`, 'gi');
  const parts = String(text).split(re);
  return (
    <>
      {parts.map((p, i) => (
        re.test(p) ? <mark key={i} className="bg-amber-200/70 text-slate-900 rounded-sm px-0.5">{p}</mark> : <span key={i}>{p}</span>
      ))}
    </>
  );
};

export const AdvancedSearchDialog = ({ open, onOpenChange }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  // Focus on open + reset
  useEffect(() => {
    if (open) {
      setQuery('');
      setResults(null);
      setError(null);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Debounced search — 300ms after last keystroke
  useEffect(() => {
    if (!open) return;
    clearTimeout(debounceRef.current);
    if (query.trim().length < 2) {
      setResults(null);
      setError(null);
      setSearching(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      setError(null);
      try {
        const r = await api.post('/search/global', { q: query, limit_per_group: 5 });
        setResults(r.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Search failed');
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, open]);

  const handleNavigate = (href, externalUrl) => {
    if (externalUrl) {
      window.open(externalUrl, '_blank');
      return;
    }
    if (!href) return;
    navigate(href);
    onOpenChange(false);
  };

  // Combine phrases + keywords for the highlight, with quoted phrases first
  // (longer match wins under regex alternation).
  const terms = results
    ? [...(results.phrases || []), ...(results.keywords || [])].sort((a, b) => b.length - a.length)
    : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl p-0 overflow-hidden gap-0"
        aria-describedby="adv-search-desc"
        data-testid="advanced-search-dialog"
      >
        {/* Search bar */}
        <div className="relative border-b border-slate-200 dark:border-slate-700">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder='Search the whole CRM — try a name, postcode, or "exact phrase"'
            className="w-full pl-11 pr-14 py-4 text-sm bg-transparent focus:outline-none placeholder:text-slate-400"
            data-testid="advanced-search-input"
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
            {searching && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
            {query && !searching && (
              <button onClick={() => setQuery('')} className="text-slate-400 hover:text-slate-700 p-1" aria-label="Clear">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <p id="adv-search-desc" className="sr-only">Advanced search across customers, leads, emails, notes, tickets, documents and helpful links.</p>
        </div>

        {/* Body */}
        <div className="max-h-[60vh] overflow-y-auto">
          {/* Empty state — tips */}
          {!query.trim() && (
            <div className="px-5 py-6 text-sm text-slate-500 space-y-2.5" data-testid="search-empty-tips">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-500" />
                <div className="space-y-1.5">
                  <p className="font-medium text-slate-700 dark:text-slate-200">Tips</p>
                  <ul className="space-y-0.5 text-xs">
                    <li>• Words combine with <strong>AND</strong> — every keyword must match.</li>
                    <li>• Wrap phrases in <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded text-[10px]">"quotes"</code> for an exact match. e.g. <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded text-[10px]">"final invoice"</code></li>
                    <li>• Mix freely: <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded text-[10px]">nick "final invoice" oak avenue</code></li>
                    <li>• Searches: customers · leads · emails · notes · tickets · documents · links.</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Loading */}
          {query.trim().length >= 2 && !results && searching && !error && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
            </div>
          )}

          {/* Too-short hint */}
          {query.trim().length === 1 && (
            <p className="px-5 py-6 text-xs text-slate-400 italic">Type at least 2 characters to start searching…</p>
          )}

          {/* Error */}
          {error && (
            <p className="px-5 py-6 text-xs text-rose-500" data-testid="search-error">{error}</p>
          )}

          {/* Results */}
          {results && (
            <div className="divide-y divide-slate-100 dark:divide-slate-800" data-testid="search-results">
              {results.total === 0 ? (
                <p className="px-5 py-8 text-center text-sm text-slate-400">
                  No matches for <span className="font-mono text-slate-600">{query}</span>
                </p>
              ) : (
                <>
                  <div className="px-5 py-2 text-[11px] text-slate-500 bg-slate-50 dark:bg-slate-900 flex items-center justify-between">
                    <span><strong>{results.total}</strong> result{results.total === 1 ? '' : 's'} across {results.groups.length} {results.groups.length === 1 ? 'category' : 'categories'}</span>
                    {results.phrases?.length > 0 && (
                      <span className="flex items-center gap-1">
                        <Quote className="w-3 h-3" />
                        Exact phrase: {results.phrases.map((p) => <code key={p} className="ml-1 bg-amber-100 text-amber-800 px-1 rounded text-[10px]">{p}</code>)}
                      </span>
                    )}
                  </div>
                  {results.groups.map((g) => (
                    <div key={g.key} className="px-2 py-2" data-testid={`group-${g.key}`}>
                      <p className="px-3 pb-1 text-[10px] uppercase tracking-wider font-semibold text-slate-500">
                        {g.label} ({g.items.length})
                      </p>
                      {g.items.map((it) => (
                        <button
                          key={it.id || it.href}
                          onClick={() => handleNavigate(it.href, it.external_url)}
                          className="w-full text-left px-3 py-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 group transition-colors"
                          data-testid={`result-${g.key}-${it.id || ''}`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                                <Highlight text={it.title} terms={terms} />
                              </p>
                              {it.subtitle && (
                                <p className="text-xs text-slate-500 truncate">
                                  <Highlight text={it.subtitle} terms={terms} />
                                </p>
                              )}
                            </div>
                            {it.badge && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 capitalize flex-shrink-0">
                                {String(it.badge).replace(/_/g, ' ')}
                              </span>
                            )}
                            {it.external_url
                              ? <ExternalLink className="w-3.5 h-3.5 text-slate-300 group-hover:text-blue-500 flex-shrink-0" />
                              : <ArrowRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-blue-500 flex-shrink-0" />}
                          </div>
                        </button>
                      ))}
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div className="border-t border-slate-200 dark:border-slate-700 px-4 py-2 bg-slate-50/50 dark:bg-slate-900 text-[10px] text-slate-500 flex items-center justify-between">
          <span>Quoted phrases match exactly. AND combines keywords.</span>
          <span className="font-mono">⇧⌘F · ESC to close</span>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AdvancedSearchDialog;
